// The merge, shown before it is done. Everything the server would move,
// take, disable or leave is on this screen, and the button sends the
// token of exactly this picture - a field moves only when the admin ticks
// it, and every warning is a box they have to tick first.

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Dialog } from "../chrome/Dialog";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import { useMergeCases, useMergePreview } from "../../hooks/useCaseTwins";
import {
  MergeRefusedError,
  type CaseTwin,
  type MergeOutcome,
  type MergePreview,
  type Wire,
} from "../../api/duplicates";

const PATH_LABELS: Record<string, string> = {
  "clientData.personal.firstName": "First name",
  "clientData.personal.lastName": "Last name",
  "clientData.personal.middleName": "Middle name",
  "clientData.personal.dateOfBirth": "Date of birth",
  "clientData.personal.alienNumber": "A-number",
  "clientData.immigration.countryOfBirth": "Country of birth",
  "clientData.contact.phone": "Phone",
  "clientData.contact.altPhone": "Alternate phone",
  "clientData.contact.email": "Email",
  "clientData.address.street": "Street",
  "clientData.address.city": "City",
  "clientData.address.state": "State",
  "clientData.address.zip": "ZIP",
  "clientData.address.country": "Country",
  caseType: "Case type",
  caseStage: "Stage",
  priority: "Priority",
  practiceAreaTemplateId: "Practice-area template",
  "immigrationSpecific.judgeId": "Judge",
  "immigrationSpecific.judgeName": "Judge (name)",
  "immigrationSpecific.courtAddress": "Court address",
  "immigrationSpecific.isDetained": "Detained",
  notes: "Notes (appended)",
  "immigrationSpecific.nextHearing": "Next hearing",
  clientId: "Client record",
  "syncStatus.contactId": "Google contact",
  "syncStatus.driveFolderId": "Drive folder",
  "syncStatus.driveFolder": "Drive folder name",
  "syncStatus.lastDriveSync": "Last Drive sync",
  needsReview: "Needs review",
};

const MOVE_LABELS: Record<string, string> = {
  documents: "documents",
  intakeSessions: "intake sessions",
  invoices: "invoices",
  payments: "payments",
  plans: "payment plans",
  messages: "messages",
  motions: "motions",
  packages: "filing packages",
  comments: "comments",
  activities: "activity entries",
  calls: "call logs",
  alerts: "deadline alerts",
  mail: "emails",
  conversations: "AI conversations",
  emailLinks: "Gmail links",
  contactMatches: "contact matches",
  counsel: "opposing counsel",
  family: "family links",
};

const ARRAY_LABELS: Record<string, string> = {
  deadlines: "Deadlines",
  calendarEvents: "Calendar events",
  team: "Team",
  criminalHistory: "Criminal history",
};

const PORTAL_WORDS: Record<MergePreview["portal"]["action"], string> = {
  moved:
    "The client's portal login moves to the surviving matter; they keep the credential they were sent.",
  disabled:
    "Both matters have a portal login. The folded one is disabled; the client needs a new invitation from the surviving matter.",
  none: "Neither matter has a portal login.",
};

const DRIVE_WORDS: Record<MergePreview["drive"]["action"], string> = {
  moved: "The Drive folder moves to the surviving matter.",
  cleared: "Both point at the same Drive folder; the folded matter lets go of it.",
  left: "Each matter has its own Drive folder. The folded one keeps its pointer until the folders are merged in Drive.",
  none: "Neither matter has a Drive folder.",
};

const label = (path: string) => PATH_LABELS[path] ?? path;
const ID_PATHS = new Set([
  "clientData.personal.alienNumber",
  "immigrationSpecific.judgeId",
  "clientId",
  "syncStatus.contactId",
  "syncStatus.driveFolderId",
]);

function show(v: Wire): string {
  if (v === null) return "-";
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** The first of the two: the matter on screen, or the pair's first side off it. */
  current: { caseId: string; eId: string | null };
  twin: CaseTwin;
}

export function MergeDialog({ open, onClose, current, twin }: Props) {
  const navigate = useNavigate();
  // "This matter" is only the one the page is about; the firm list has none.
  const pageEId = useParams().eID ?? null;
  const here = (eId: string | null) => eId !== null && eId === pageEId;
  const [keepCurrent, setKeepCurrent] = useState(true);
  const [take, setTake] = useState<Set<string>>(new Set());
  const [arrays, setArrays] = useState<Set<string>>(new Set());
  const [acked, setAcked] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<{ text: string; bad: boolean } | null>(null);
  const [done, setDone] = useState<MergeOutcome | null>(null);

  const keepCaseId = keepCurrent ? current.caseId : twin.caseId;
  const mergeCaseId = keepCurrent ? twin.caseId : current.caseId;
  const preview = useMergePreview(keepCaseId, mergeCaseId, open && done === null);
  const merge = useMergeCases();

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setter(next);
  };

  function swap() {
    setKeepCurrent((k) => !k);
    setTake(new Set());
    setArrays(new Set());
    setAcked(new Set());
    setNotice(null);
  }

  function close() {
    setNotice(null);
    setDone(null);
    onClose();
  }

  const p = preview.data;
  const blocked = (p?.blockers.length ?? 0) > 0;
  const owned = p ? p.warnings.every((w) => acked.has(w.code)) : false;
  const ready = Boolean(p) && !blocked && owned && !merge.isPending;

  function submit() {
    if (!p) return;
    setNotice(null);
    merge.mutate(
      {
        keepCaseId,
        mergeCaseId,
        previewToken: p.previewToken,
        take: [...take],
        arrays: Object.fromEntries([...arrays].map((n) => [n, true])),
        acknowledgedWarnings: [...acked],
      },
      {
        onSuccess: (result) => setDone(result),
        onError: (err) => {
          const refused = err instanceof MergeRefusedError ? err : null;
          // Every 409 means the server sees a different picture now; the
          // warnings are asked again over the reloaded one.
          if (refused?.status === 409) {
            setAcked(new Set());
            void preview.refetch();
          }
          setNotice({
            text:
              refused?.code === "preview_stale"
                ? "The matters changed since this preview; it has been reloaded - look again."
                : err instanceof Error
                  ? err.message
                  : "The merge failed",
            bad: true,
          });
        },
      },
    );
  }

  // The portal has its own sentence; the count line is the rows that moved.
  const moved = done
    ? Object.entries(done.counts)
        .filter(([id, n]) => n > 0 && id !== "portal")
        .map(([id, n]) => `${n} ${MOVE_LABELS[id] ?? id}`)
    : [];
  const survivor = done?.keepCaseId === current.caseId ? current : twin;
  const survivorHere = done !== null && here(survivor.eId);

  return (
    <Dialog open={open} onClose={close} title="Merge duplicate matters" locked={merge.isPending}>
      {done && (
        <div className="merge-done">
          <p>
            Merged <code>{done.identifiers.retiredEId ?? done.archivedCaseId}</code> into{" "}
            {survivorHere ? "this matter" : <code>{survivor.eId ?? done.keepCaseId}</code>}
            {moved.length > 0 ? `: ${moved.join(", ")}` : ""}.
            {done.resumed &&
              moved.length === 0 &&
              " This merge had already run; nothing was left to move."}
          </p>
          {done.picks.ignored && (
            <p className="merge-notice">
              The ticked picks were not applied: this merge had already completed.
            </p>
          )}
          {done.portal.needsReinvite && (
            <p className="merge-notice">
              The client's portal login was disabled. Send a new invitation from the surviving
              matter's client access card.
            </p>
          )}
          <div className="merge-foot">
            <span />
            <span style={{ display: "flex", gap: 8 }}>
              {!survivorHere && survivor.eId && (
                <button
                  type="button"
                  className="cc-btn-primary"
                  onClick={() => {
                    onClose();
                    navigate(`/matter/${survivor.eId}`);
                  }}
                >
                  Open {survivor.eId}
                </button>
              )}
              <button
                type="button"
                className={survivorHere ? "cc-btn-primary" : "cc-link-btn"}
                onClick={close}
              >
                Close
              </button>
            </span>
          </div>
        </div>
      )}

      {!done && (
        <>
          <div className="merge-keep" role="radiogroup" aria-label="Which matter survives">
            {/* One span per sentence: the label is a flex row and its gap
                would otherwise open before every comma. */}
            <label>
              <input type="radio" name="merge-direction" checked={keepCurrent} onChange={swap} />
              <span>
                Keep <code>{current.eId ?? current.caseId}</code>
                {here(current.eId) ? " (this matter), fold " : ", fold "}
                <code>{twin.eId ?? twin.caseId}</code>
              </span>
            </label>
            <label>
              <input type="radio" name="merge-direction" checked={!keepCurrent} onChange={swap} />
              <span>
                Keep <code>{twin.eId ?? twin.caseId}</code>
                {here(current.eId) ? ", fold this matter" : ", fold "}
                {!here(current.eId) && <code>{current.eId ?? current.caseId}</code>}
              </span>
            </label>
          </div>

          {preview.isLoading && (
            <SkeletonGroup>
              <Skeleton width={280} />
              <Skeleton width={220} />
              <Skeleton width={240} />
            </SkeletonGroup>
          )}
          {preview.isError && (
            <p className="merge-notice bad">{(preview.error as Error).message}</p>
          )}

          {p && (
            <>
              {blocked && (
                <section className="merge-section merge-blockers" role="alert">
                  <h4>Cannot merge</h4>
                  {p.blockers.map((b) => (
                    <div key={b.code}>{b.message}</div>
                  ))}
                </section>
              )}

              <section className="merge-section">
                <h4>What moves to {p.keep.eId ? <code>{p.keep.eId}</code> : "the survivor"}</h4>
                <div className="merge-moves">
                  {p.moves.filter((m) => m.count > 0).length === 0 && <span>Nothing owned</span>}
                  {p.moves
                    .filter((m) => m.count > 0)
                    .map((m) => (
                      <span key={m.id}>
                        <b className="tnum">{m.count}</b> {MOVE_LABELS[m.id] ?? m.id}
                      </span>
                    ))}
                </div>
                <div className="merge-notice">{PORTAL_WORDS[p.portal.action]}</div>
                <div className="merge-notice">{DRIVE_WORDS[p.drive.action]}</div>
              </section>

              {p.divergence.length > 0 && (
                <section className="merge-section">
                  <h4>Facts that differ - tick to take the folded matter's</h4>
                  <table className="merge-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>{p.keep.eId ? <code>{p.keep.eId}</code> : "Survivor"} keeps</th>
                        <th>
                          Take from {p.merge.eId ? <code>{p.merge.eId}</code> : "the folded matter"}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.divergence.map((d) => (
                        <tr key={d.path}>
                          <td>{label(d.path)}</td>
                          <td className={ID_PATHS.has(d.path) ? "tnum-mono" : undefined}>
                            {show(d.keep)}
                          </td>
                          <td className="merge-take">
                            <label>
                              <input
                                type="checkbox"
                                checked={take.has(d.path)}
                                onChange={() => toggle(take, setTake, d.path)}
                                aria-label={`Take ${label(d.path)}`}
                              />
                              <span className={ID_PATHS.has(d.path) ? "tnum-mono" : undefined}>
                                {show(d.merge)}
                              </span>
                            </label>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              {p.arrays.some((a) => a.wouldAdd > 0) && (
                <section className="merge-section">
                  <h4>Lists to combine</h4>
                  {p.arrays
                    .filter((a) => a.wouldAdd > 0)
                    .map((a) => (
                      <label key={a.name} className="merge-warning">
                        <input
                          type="checkbox"
                          checked={arrays.has(a.name)}
                          onChange={() => toggle(arrays, setArrays, a.name)}
                        />
                        <span>
                          {ARRAY_LABELS[a.name] ?? a.name}: add {a.wouldAdd} of the folded matter's{" "}
                          {a.mergeCount} to the survivor's {a.keepCount}
                        </span>
                      </label>
                    ))}
                </section>
              )}

              {p.carryUps.length > 0 && (
                <section className="merge-section">
                  <h4>Filled in on the survivor</h4>
                  <div className="merge-moves">
                    {p.carryUps.map((c) => (
                      <span key={c.path}>
                        {label(c.path)}:{" "}
                        <b className={ID_PATHS.has(c.path) ? "tnum-mono" : undefined}>
                          {show(c.value)}
                        </b>
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {p.warnings.length > 0 && (
                <section className="merge-section">
                  <h4>Before merging, confirm</h4>
                  {p.warnings.map((w) => (
                    <label key={w.code} className="merge-warning">
                      <input
                        type="checkbox"
                        checked={acked.has(w.code)}
                        onChange={() => toggle(acked, setAcked, w.code)}
                      />
                      <span>{w.message}</span>
                    </label>
                  ))}
                </section>
              )}
            </>
          )}

          <div className="merge-foot">
            <span className={"merge-notice" + (notice?.bad ? " bad" : "")} role="status">
              {notice?.text ?? ""}
            </span>
            <button type="button" className="cc-btn-primary" disabled={!ready} onClick={submit}>
              {merge.isPending ? "Merging..." : "Merge"}
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}
